import Interaction, {InteractionType} from "./Interaction";
import {GetNewGuid} from "../../helpers/GuidHelper";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { ValidationHelper } from "../../features/validation/ValidationHelper";
import TransitionNodePreview from "../../features/sequence/node_previews/TransitionNodePreview";
import TransitionPreview from "../../features/previews/transition/TransitionPreview";
import {
    createDefaultTypewriterSettings,
    DEFAULT_TRANSITION_DURATION,
    normalizeTransition,
} from "./TransitionDefaults";

export type TransitionType = "default" | "typewriter";

export interface ITypewriterTransitionSettings {
    instantText: boolean;
    title: string;              // reserved localized key ("" until allocated)
    text: string;               // reserved localized key ("" until allocated)
    titleIconAssetId: string;
    bottomIconAssetId: string;
    fontSize: number;
    transitionSpeedStart: number;
    transitionSpeedStop: number;
    typingSpeed: number;
    showButtonDelay: number;
    useBackgroundColor: boolean;
    backgroundColor: {r: number, g: number, b: number, a: number};
    useTextColor: boolean;
    textColor: {r: number, g: number, b: number, a: number};
}

export interface ITransition extends Interaction {
    text: string;
    assetId: string;
    duration: number;
    color: {r: number, g: number, b: number, a: number}
    transitionType: TransitionType;
    typewriter: ITypewriterTransitionSettings;
}

export function CreateTransition(key: string): ITransition {
    return {
        id: GetNewGuid(),
        type: InteractionType.Transition,
        name: "Transition",
        color: {r: 0, g: 0, b: 0, a: 1},
        assetId: "",
        duration: DEFAULT_TRANSITION_DURATION,
        text: key,
        transitionType: "default",
        typewriter: createDefaultTypewriterSettings(),
    }
}

export const transitionInteraction = (): InteractionFactoryEntity => {
    const component = <TransitionPreview />;

    const create = (getKeys: (amount: number) => string[]): ITransition => {
        const keys = getKeys(1);

        return {
            id: GetNewGuid(),
            type: InteractionType.Transition,
            name: "Transition",
            color: {r: 0, g: 0, b: 0, a: 1},
            assetId: "",
            duration: DEFAULT_TRANSITION_DURATION,
            text: keys[0],
            transitionType: "default",
            typewriter: createDefaultTypewriterSettings(),
        };
    };

    const migrate = (
        from: number,
        to: number,
        interaction: Interaction
    ): ITransition => {
        return normalizeTransition(interaction as ITransition);
    };

    const copy = (
        interaction: Interaction,
        getKeys: (amount: number) => string[],
        replaceKey: (from: string, to: string) => void
    ): ITransition => {
        const source = normalizeTransition(interaction as ITransition);
        const hasTitle = source.typewriter.title !== "";
        const hasTypewriterText = source.typewriter.text !== "";
        const keys = getKeys(
            1 + (hasTitle ? 1 : 0) + (hasTypewriterText ? 1 : 0)
        );

        let i = 0;
        const textKey = keys[i++];
        replaceKey(source.text, textKey);

        const titleKey = hasTitle ? keys[i++] : "";
        if (hasTitle) replaceKey(source.typewriter.title, titleKey);

        const typewriterTextKey = hasTypewriterText ? keys[i++] : "";
        if (hasTypewriterText)
            replaceKey(source.typewriter.text, typewriterTextKey);

        return {
            ...source,
            id: GetNewGuid(),
            text: textKey,
            typewriter: {
                ...source.typewriter,
                title: titleKey,
                text: typewriterTextKey,
            },
        };
    };

    const validate = (
        interaction: Interaction,
        getValue: (key: string) => string
    ): string[] => {
        const transition = normalizeTransition(interaction as ITransition);
        const warnings: string[] = [];

        if (transition.transitionType === "typewriter") {
            ValidationHelper.ValidateKey(transition.typewriter.title, getValue, warnings, "Title");
            ValidationHelper.ValidateKey(transition.typewriter.text, getValue, warnings, "Text");
        } else {
            ValidationHelper.ValidateKey(transition.text, getValue, warnings, "Text");
        }

        return warnings;
    };

    const validateErrors = (interaction: Interaction): string[] => {
        const transition = normalizeTransition(interaction as ITransition);
        const errors: string[] = [];

        if (transition.transitionType === "typewriter") {
            if (!transition.typewriter.text?.trim()) errors.push("Text is required");
        } else {
            if (!transition.text?.trim()) errors.push("Text is required");
        }

        return errors;
    };

    const filter = (
        interaction: Interaction,
        takeName: string,
        getMatchingResults: (
            searchableStrings: string[],
            takeName: string,
            interaction: Interaction,
            filterType: string
        ) => SearchResultData[]
    ): SearchResultData[] => {
        const converted = interaction as ITransition;
        const searchableStrings: string[] = [interaction.name, converted.text];
        if (converted.typewriter?.title) searchableStrings.push(converted.typewriter.title);
        if (converted.typewriter?.text) searchableStrings.push(converted.typewriter.text);

        return getMatchingResults(
            searchableStrings,
            takeName,
            interaction,
            filterType
        );
    };

    const filterType: string = "Transition";

    return {
        View: component,
        NodeView: (nodeId: string, interaction: Interaction) => (
            <TransitionNodePreview nodeId={nodeId} interaction={interaction} />
        ),
        FilterType: filterType,
        Create: create,
        Migrate: migrate,
        Copy: copy,
        Validate: validate,
        ValidateErrors: validateErrors,
        Filter: filter,
    };
};
